import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import serviceClient from 'src/js/modules/service-client/service-client';
import { SiteSetupContext } from 'src/js/modules/site-setup/helpers/site-setup-context';
import trackers from 'src/js/modules/analytics/segment/trackers';
import siteSetup from 'src/js/modules/site-setup';
import error from 'src/js/modules/error';
import usePreviousValue from '../../common/hooks/use-previous-value';
import { SOURCE_ID } from '../../constants/common';

const { AutocompleteField, Icon } = designSystem;

const { trackBusinessCategoryInputFocused, trackBusinessCategorySelected } = trackers;
const { constants } = siteSetup;
const { siteSetupNames } = constants;

const MAX_MENU_HEIGHT = 352;

const defaultPropCaptions = {
  placeholder: '',
  noResultsText: '',
};

const BusinessCategoryField = ({
  name,
  control,
  captions = defaultPropCaptions,
  size = 'large',
  stepId,
  siteId = null,
  locale,
  openMenu = false,
  validate = null,
  onChange = null,
  onInput = null,
}) => {
  const context = useContext(SiteSetupContext);
  const { getData } = context;
  const contextData = getData();

  const [businessCategoryValue, setBusinessCategoryValue] = useState(
    contextData.businessCategory || null
  );
  const prevBusinessCategoryValue = usePreviousValue(businessCategoryValue);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(
    contextData.businessCategory
      ? {
          label: contextData.businessCategory,
          value: contextData.businessCategorySlug,
          archetype: contextData.archetype,
          templates: contextData.templates || [],
        }
      : null
  );

  const loadOptions = async (inputValue) => {
    let options = [{}];

    setDefaultOptions([]);
    setIsLoading(true);

    try {
      const client = serviceClient.get();
      const { data } = await client.taxonomySearchPublicCategories({
        searchQuery: inputValue,
        locale,
      });

      if (data?.results) {
        options = data.results.map(({ title, category, archetypes, templates }) => ({
          label: title,
          value: category,
          archetype: archetypes[0],
          templates,
        }));
      }
    } catch (e) {
      error.operations.catchError(e);
    }

    setIsLoading(false);
    setDefaultOptions(options);
    return options;
  };

  const handleChange = (option) => {
    if (option && option.value) {
      setSelectedOption(option);
      trackBusinessCategorySelected({
        siteId,
        stepId,
        businessCategory: option.label,
        oldBusinessCategory: contextData.businessCategory,
        archetypeId: option.archetype,
        source: SOURCE_ID,
      });

      if (onChange) {
        onChange(option);
      }
    }

    setBusinessCategoryValue(option?.label || '');
  };

  const handleInputChange = (value) => {
    setBusinessCategoryValue(value);

    if (onInput) {
      onInput();
    }
  };

  const handleFocus = () => {
    trackBusinessCategoryInputFocused({
      siteId,
      stepId,
      source: SOURCE_ID,
      businessCategory: selectedOption?.value || null,
    });
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (businessCategoryValue && businessCategoryValue !== prevBusinessCategoryValue) {
      loadOptions(businessCategoryValue);
    }
  };

  useEffect(() => {
    if (businessCategoryValue) {
      loadOptions(businessCategoryValue);
    }
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <AutocompleteField
      name={name}
      {...(validate && {
        rules: { validate: (value) => validate(value, businessCategoryValue) },
      })}
      asyncConfig={{
        loadOptions,
        defaultOptions,
        isLoading,
        cacheOptions: true,
      }}
      iconComponent={<Icon glyph="search" type="inherit" stroke="#141B21" />}
      placeholder={captions.placeholder}
      control={control}
      size={size}
      options={[]}
      inputValue={businessCategoryValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      menuIsOpen={
        (openMenu && Boolean(businessCategoryValue)) ||
        (isFocused && Boolean(businessCategoryValue))
      }
      openMenuOnFocus={Boolean(businessCategoryValue)}
      filterOptionsFunction={(value) => value}
      maxMenuHeight={MAX_MENU_HEIGHT}
      noResultsText={captions.noResultsText}
      blurInputOnSelect
      shouldMarkSelectedOption
    />
  );
};

BusinessCategoryField.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.shape({}).isRequired,
  captions: PropTypes.shape({
    placeholder: PropTypes.string,
    noResultsText: PropTypes.string,
  }),
  stepId: PropTypes.oneOf(Object.values(siteSetupNames)).isRequired,
  siteId: PropTypes.string,
  locale: PropTypes.string.isRequired,
  size: PropTypes.string,
  openMenu: PropTypes.bool,
  validate: PropTypes.func,
  onChange: PropTypes.func,
  onInput: PropTypes.func,
};

export default BusinessCategoryField;
